/**
 * Composer harness + model options from live presence (RFC 0269).
 *
 * The picker lists harnesses (including Mod's native proxy path), then models
 * for the selected harness. Presence-backed harnesses appear when a desktop is
 * connected; Mod is always offered so a machine without a CLI can still send.
 */

import type { DevicePresenceRef } from '../../model/presence/reads'
import type { HarnessPresence } from '../../model/presence/types'
import { COMPOSER_MODELS, type ComposerModel } from '../llm/inference-client'

/** One selectable entry: harness id as provider (or openai for Mod), model id. */
export interface HarnessComposerOption extends ComposerModel {
  /** Harness label for grouped UI ("Claude Code", "Cursor", "Mod"). */
  harnessLabel: string
}

/** One harness column entry for the two-pane picker. */
export interface ComposerHarnessGroup {
  /** Stable id: "mod" | "claude-code" | "codex" | "cursor" | … */
  id: string
  label: string
  /** One-line detail under the name (Apps-modal row sentence). */
  detail: string
  models: ComposerModel[]
}

export const MOD_HARNESS_ID = 'mod'

const MOD_GROUP: ComposerHarnessGroup = {
  id: MOD_HARNESS_ID,
  label: 'Mod',
  detail: 'Mod’s own models via the proxy.',
  models: COMPOSER_MODELS.map((m) => ({
    ...m,
    // Flat ids stay openai-shaped so the proxy resolve path is unchanged.
  })),
}

const HARNESS_DETAIL: Record<string, string> = {
  'claude-code': 'Your installed Claude Code and plan.',
  codex: 'Your installed Codex CLI and ChatGPT plan.',
  cursor: 'Your installed Cursor agent CLI.',
  pi: 'Your installed Pi and the providers it is signed in to.',
}

/**
 * Driveable + connected harnesses across present devices, de-duped by harness
 * id (first device wins for models/label).
 */
export function driveableHarnessesFromDevices(
  devices: Array<Pick<DevicePresenceRef, 'harnesses'>>,
): HarnessPresence[] {
  const byId = new Map<string, HarnessPresence>()
  for (const device of devices) {
    for (const h of device.harnesses ?? []) {
      if (h.state !== 'connected') continue
      if (h.driveable === false) continue
      if (!byId.has(h.id)) byId.set(h.id, h)
    }
  }
  return [...byId.values()]
}

export function composerOptionsFromHarnesses(
  harnesses: HarnessPresence[],
): HarnessComposerOption[] {
  const out: HarnessComposerOption[] = []
  for (const h of harnesses) {
    const models = h.models && h.models.length > 0 ? h.models : ['default']
    for (const model of models) {
      out.push({
        id: `${h.id}:${model}`,
        label: model === 'default' ? h.label : shortModel(model),
        provider: h.id,
        model,
        harnessLabel: h.label,
      })
    }
  }
  return out
}

function shortModel(model: string): string {
  if (model.length <= 28) return model
  return `${model.slice(0, 25)}…`
}

function groupFromPresence(h: HarnessPresence): ComposerHarnessGroup {
  const models = composerOptionsFromHarnesses([h]).map((m) => ({
    id: m.id,
    label: m.label,
    provider: m.provider,
    model: m.model,
  }))
  return {
    id: h.id,
    label: h.label,
    detail: HARNESS_DETAIL[h.id] ?? 'Installed on this machine.',
    models,
  }
}

/**
 * Prepend a synthetic "this device" entry so the picker can show locally probed
 * harnesses before World presence has synced (desktop shell fast path).
 * `driveableHarnessesFromDevices` de-dupes by id with first-wins, so local
 * entries take priority over a later presence beat for the same harness.
 */
export function withLocalHarnessDevices<
  T extends Pick<DevicePresenceRef, 'harnesses'>,
>(
  devices: T[],
  local: HarnessPresence[],
): Array<T | { deviceId: string; name: string; harnesses: HarnessPresence[] }> {
  if (local.length === 0) return devices
  return [
    {
      deviceId: '__local__',
      name: 'This device',
      harnesses: local,
    },
    ...devices,
  ]
}

/**
 * Groups for the two-pane picker: Mod always, then connected driveable CLIs.
 */
export function resolveComposerHarnessGroups(
  devices: Array<Pick<DevicePresenceRef, 'harnesses'>>,
): ComposerHarnessGroup[] {
  const fromPresence =
    driveableHarnessesFromDevices(devices).map(groupFromPresence)
  return [MOD_GROUP, ...fromPresence]
}

/**
 * Flat options (every model under every group). Used by preferredComposerModel
 * and send paths that only need id → {provider, model}.
 */
export function resolveComposerOptions(
  devices: Array<Pick<DevicePresenceRef, 'harnesses'>>,
): ComposerModel[] {
  return resolveComposerHarnessGroups(devices).flatMap((g) => g.models)
}

/** Which group a stored option id belongs to. */
export function harnessIdForOption(
  option: ComposerModel | undefined,
  groups: ComposerHarnessGroup[],
): string {
  if (!option) return groups[0]?.id ?? MOD_HARNESS_ID
  if (option.provider === 'openai' || option.provider === 'anthropic') {
    return MOD_HARNESS_ID
  }
  const match = groups.find((g) => g.id === option.provider)
  return match?.id ?? MOD_HARNESS_ID
}
